import { useEffect, useState, useCallback } from "react";

const DISMISS_KEY = "pwa_dismissed_at";
const INSTALLED_KEY = "pwa_installed";
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 giorni

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true // iOS Safari legacy
  );
}

function isIosSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
  return isIos && isSafari;
}

function wasRecentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < DISMISS_COOLDOWN_MS;
  } catch {
    return false;
  }
}

/**
 * usePwaInstall — hook that exposes:
 *   - deferredPrompt: raw beforeinstallprompt event (or null)
 *   - canInstall: true if we can trigger native prompt now (Chromium/Android/Desktop)
 *   - isInstalled: PWA already running as standalone or previously installed
 *   - isIosSafari: platform requires manual "Add to Home Screen" instructions
 *   - install(): trigger native prompt; resolves to boolean (true = accepted)
 *   - dismiss(): mark banner dismissed for 7 days
 */
export default function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(() => isStandalone() || localStorage.getItem(INSTALLED_KEY) === "true");
  const [dismissedRecent, setDismissedRecent] = useState(() => wasRecentlyDismissed());

  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      try { localStorage.setItem(INSTALLED_KEY, "true"); } catch {}
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt) return false;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    if (outcome === "accepted") {
      try { localStorage.setItem(INSTALLED_KEY, "true"); } catch {}
      setInstalled(true);
      return true;
    }
    return false;
  }, [deferredPrompt]);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setDismissedRecent(true);
  }, []);

  const canInstall = !!deferredPrompt && !installed;
  return {
    canInstall,
    installed,
    dismissedRecent,
    isIosSafari: isIosSafari() && !installed,
    install,
    dismiss,
    deferredPrompt,
  };
}
